import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDollarSign, FiClock, FiTrendingUp, FiCheckCircle, FiAlertTriangle, FiZap, FiTarget, FiActivity } = FiIcons;

const Savings = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  // Time Savings Data (hours per month)
  const timeSavingsData = {
    dataEntry: {
      category: 'Data Entry & Processing',
      tasks: [
        { name: 'Pastel Data Import', manualHours: 60, automatedHours: 2, savings: 58, description: 'Daily stock & price updates' },
        { name: 'Supplier Price Updates', manualHours: 40, automatedHours: 1, savings: 39, description: 'CSV/Email price imports' },
        { name: 'Order Entry', manualHours: 80, automatedHours: 5, savings: 75, description: 'WhatsApp order processing' },
        { name: 'Stock Level Monitoring', manualHours: 30, automatedHours: 0.5, savings: 29.5, description: 'Automated threshold alerts' }
      ]
    },
    communication: {
      category: 'Communication & Follow-ups',
      tasks: [
        { name: 'Customer Queries', manualHours: 120, automatedHours: 10, savings: 110, description: 'WhatsApp bot responses' },
        { name: 'Debtor Reminders', manualHours: 25, automatedHours: 1, savings: 24, description: 'Automated payment reminders' },
        { name: 'Order Confirmations', manualHours: 35, automatedHours: 2, savings: 33, description: 'Auto delivery confirmations' },
        { name: 'Customer Follow-ups', manualHours: 40, automatedHours: 1, savings: 39, description: 'Reorder reminders' }
      ]
    },
    financial: {
      category: 'Financial Processing',
      tasks: [
        { name: 'Invoice Generation', manualHours: 50, automatedHours: 2, savings: 48, description: 'Auto invoice from delivery' },
        { name: 'POD Management', manualHours: 20, automatedHours: 0.5, savings: 19.5, description: 'Automated POD storage' },
        { name: 'Daily Reporting', manualHours: 15, automatedHours: 0.5, savings: 14.5, description: 'Auto management reports' },
        { name: 'AR Aging Analysis', manualHours: 10, automatedHours: 0.5, savings: 9.5, description: 'Automated AR tracking' }
      ]
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalManual = 0;
    let totalAutomated = 0;
    let totalSavings = 0;

    Object.values(timeSavingsData).forEach(category => {
      category.tasks.forEach(task => {
        totalManual += task.manualHours;
        totalAutomated += task.automatedHours;
        totalSavings += task.savings;
      });
    });

    return { totalManual, totalAutomated, totalSavings };
  };

  const totals = calculateTotals();

  // Financial Impact
  const hourlyRate = 150; // N$ per hour (average labor cost)
  const monthlyLaborSavings = totals.totalSavings * hourlyRate;
  const yearlyLaborSavings = monthlyLaborSavings * 12;

  // Additional financial benefits
  const financialBenefits = {
    dsoReduction: {
      name: 'DSO Reduction',
      oldDays: 45,
      newDays: 28,
      improvement: 17,
      monthlySaving: 85000,
      description: 'Faster payment collection through automated reminders'
    },
    stockoutPrevention: {
      name: 'Stockout Prevention',
      oldLosses: 45000,
      newLosses: 5000,
      saving: 40000,
      description: 'Automated stock alerts prevent lost sales'
    },
    pricingAccuracy: {
      name: 'Pricing Accuracy',
      oldMarginLoss: 35000,
      newMarginLoss: 3000,
      saving: 32000,
      description: 'Real-time price updates protect margins'
    },
    orderErrors: {
      name: 'Order Error Reduction',
      oldCost: 25000,
      newCost: 2000,
      saving: 23000,
      description: 'Automated validation reduces costly mistakes'
    }
  };

  const totalFinancialSavings =
    monthlyLaborSavings +
    financialBenefits.dsoReduction.monthlySaving +
    financialBenefits.stockoutPrevention.saving +
    financialBenefits.pricingAccuracy.saving +
    financialBenefits.orderErrors.saving;

  // Accuracy improvements
  const accuracyMetrics = [
    { metric: 'Data Entry Accuracy', before: 92, after: 99.7, improvement: 7.7, unit: '%' },
    { metric: 'Pricing Accuracy', before: 88, after: 99.9, improvement: 11.9, unit: '%' },
    { metric: 'Order Processing Accuracy', before: 94, after: 99.5, improvement: 5.5, unit: '%' },
    { metric: 'Invoice Accuracy', before: 90, after: 99.8, improvement: 9.8, unit: '%' }
  ];

  // Exception monitoring
  const exceptionStats = {
    month: [
      { type: 'Oversized Orders', caught: 23, prevented: 'N$ 145,000 in overstock' },
      { type: 'Pricing Anomalies', caught: 31, prevented: 'N$ 87,500 margin loss' },
      { type: 'Credit Limit Breaches', caught: 18, prevented: 'N$ 234,000 bad debt risk' },
      { type: 'Stock Threshold Alerts', caught: 67, prevented: 'N$ 156,000 in stockouts' },
      { type: 'Duplicate Orders', caught: 12, prevented: 'N$ 67,000 in errors' }
    ]
  };

  // Time savings breakdown for chart
  const timeSavingsChart = Object.values(timeSavingsData).map(category => {
    const categoryTotal = category.tasks.reduce((sum, task) => sum + task.savings, 0);
    // Shorten names for better chart readability
    const shortName = category.category
      .replace('Data Entry & Processing', 'Data Entry')
      .replace('Communication & Follow-ups', 'Communication')
      .replace('Financial Processing', 'Financial');
    return {
      name: shortName,
      fullName: category.category,
      hours: categoryTotal
    };
  });

  // ROI Calculation
  const implementationCost = 150000; // One-time setup cost
  const monthlyCost = 15000; // Running costs (hosting, maintenance)
  const paybackMonths = implementationCost / (totalFinancialSavings - monthlyCost);
  const yearlyROI = ((yearlyLaborSavings * 12 - monthlyCost * 12 - implementationCost) / implementationCost * 100);

  // Trend data (monthly savings over 6 months)
  const trendData = [
    { month: 'Month 1', savings: totalFinancialSavings * 0.4 },
    { month: 'Month 2', savings: totalFinancialSavings * 0.6 },
    { month: 'Month 3', savings: totalFinancialSavings * 0.8 },
    { month: 'Month 4', savings: totalFinancialSavings * 0.95 },
    { month: 'Month 5', savings: totalFinancialSavings },
    { month: 'Month 6', savings: totalFinancialSavings * 1.05 }
  ];

  const COLORS = ['#84cc16', '#3b82f6', '#8b5cf6', '#f59e0b'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#e8f5e9]">
            Automation <span className="text-[#84cc16]">Savings</span> Dashboard
          </h1>
          <p className="text-[#6b8772] font-mono text-sm mt-1">Track ROI, time savings, and accuracy improvements</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 bg-[#0a0f0d] border border-[#243129] rounded-lg text-[#a5c4ad] focus:ring-2 focus:ring-lime-500 focus:border-lime-500"
          >
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-lime-600/90 to-lime-700 rounded-xl p-6 text-white shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <SafeIcon icon={FiDollarSign} className="w-8 h-8" />
            <span className="text-lime-100 text-sm font-medium">Monthly</span>
          </div>
          <p className="text-sm opacity-90 mb-1">Total Savings</p>
          <p className="text-3xl font-bold">N$ {(totalFinancialSavings / 1000).toFixed(0)}k</p>
          <p className="text-sm opacity-75 mt-2">N$ {(yearlyLaborSavings / 1000).toFixed(0)}k annually</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-sky-600/90 to-sky-700 rounded-xl p-6 text-white shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <SafeIcon icon={FiClock} className="w-8 h-8" />
            <span className="text-sky-100 text-sm font-medium">Per Month</span>
          </div>
          <p className="text-sm opacity-90 mb-1">Time Saved</p>
          <p className="text-3xl font-bold">{totals.totalSavings.toFixed(0)} hrs</p>
          <p className="text-sm opacity-75 mt-2">{((totals.totalSavings / totals.totalManual) * 100).toFixed(0)}% efficiency gain</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-violet-600/90 to-violet-700 rounded-xl p-6 text-white shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <SafeIcon icon={FiTarget} className="w-8 h-8" />
            <span className="text-violet-100 text-sm font-medium">ROI</span>
          </div>
          <p className="text-sm opacity-90 mb-1">Payback Period</p>
          <p className="text-3xl font-bold">{paybackMonths.toFixed(1)} mo</p>
          <p className="text-sm opacity-75 mt-2">{yearlyROI > 0 ? '+' : ''}{yearlyROI.toFixed(0)}% Year 1 ROI</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-amber-600/90 to-amber-700 rounded-xl p-6 text-white shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <SafeIcon icon={FiCheckCircle} className="w-8 h-8" />
            <span className="text-amber-100 text-sm font-medium">Average</span>
          </div>
          <p className="text-sm opacity-90 mb-1">Accuracy Improvement</p>
          <p className="text-3xl font-bold">+{(accuracyMetrics.reduce((sum, m) => sum + m.improvement, 0) / accuracyMetrics.length).toFixed(1)}%</p>
          <p className="text-sm opacity-75 mt-2">99.7% average accuracy</p>
        </motion.div>
      </div>

      {/* Savings Trend */}
      <div className="bg-[#111916] rounded-xl border border-[#243129] p-6">
        <h3 className="text-lg font-semibold text-[#e8f5e9] mb-4">Savings Growth Trend</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1c2822" />
              <XAxis dataKey="month" tick={{ fill: '#6b8772' }} />
              <YAxis tickFormatter={(value) => `N$${(value / 1000).toFixed(0)}k`} tick={{ fill: '#6b8772' }} />
              <Tooltip
                formatter={(value) => [`N$${value.toLocaleString()}`, 'Monthly Savings']}
                contentStyle={{
                  backgroundColor: '#111916',
                  border: '1px solid #243129',
                  borderRadius: '8px',
                  color: '#e8f5e9'
                }}
              />
              <Line type="monotone" dataKey="savings" stroke="#84cc16" strokeWidth={3} dot={{ fill: '#84cc16', r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time Savings Breakdown */}
        <div className="bg-[#111916] rounded-xl border border-[#243129] p-6">
          <h3 className="text-lg font-semibold text-[#e8f5e9] mb-4">Time Savings by Category</h3>
          <div className="h-80 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timeSavingsChart} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1c2822" />
                <XAxis
                  dataKey="name"
                  angle={-20}
                  textAnchor="end"
                  height={80}
                  interval={0}
                  tick={{ fontSize: 13, fill: '#6b8772' }}
                />
                <YAxis
                  label={{ value: 'Hours Saved', angle: -90, position: 'insideLeft', fill: '#6b8772' }}
                  tick={{ fontSize: 12, fill: '#6b8772' }}
                />
                <Tooltip
                  formatter={(value) => [`${value.toFixed(0)} hours`, 'Time Saved']}
                  labelFormatter={(label, payload) => {
                    if (payload && payload[0]) {
                      return payload[0].payload.fullName;
                    }
                    return label;
                  }}
                  contentStyle={{
                    backgroundColor: '#111916',
                    border: '1px solid #243129',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#e8f5e9'
                  }}
                />
                <Bar dataKey="hours" fill="#84cc16" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-[#0a0f0d] rounded-lg p-4 border border-[#243129]">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[#a5c4ad]">Total Hours Saved</span>
              <span className="text-2xl font-bold text-lime-500">{totals.totalSavings.toFixed(0)} hrs/mo</span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-[#6b8772]">Labor Cost Savings</span>
              <span className="text-lg font-semibold text-lime-500">N$ {monthlyLaborSavings.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Financial Benefits Breakdown */}
        <div className="bg-[#111916] rounded-xl border border-[#243129] p-6">
          <h3 className="text-lg font-semibold text-[#e8f5e9] mb-4">Financial Impact Breakdown</h3>
          <div className="space-y-4">
            <div className="bg-sky-900/30 rounded-lg p-4 border border-sky-700/30">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-[#e8f5e9]">Labor Cost Reduction</h4>
                <span className="text-lg font-bold text-sky-400">N$ {(monthlyLaborSavings / 1000).toFixed(0)}k</span>
              </div>
              <p className="text-sm text-[#6b8772]">{totals.totalSavings}hrs x N${hourlyRate}/hr saved monthly</p>
            </div>

            {Object.values(financialBenefits).map((benefit, index) => (
              <motion.div
                key={benefit.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0a0f0d] rounded-lg p-4 border border-[#243129]"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-[#e8f5e9]">{benefit.name}</h4>
                  <span className="text-lg font-bold text-lime-500">
                    N$ {((benefit.saving || benefit.monthlySaving) / 1000).toFixed(0)}k
                  </span>
                </div>
                <p className="text-sm text-[#6b8772]">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Time Savings */}
      <div className="bg-[#111916] rounded-xl border border-[#243129]">
        <div className="p-6 border-b border-[#243129]">
          <h3 className="text-lg font-semibold text-[#e8f5e9]">Detailed Time Savings Analysis</h3>
          <p className="text-sm text-[#6b8772] mt-1">Task-by-task breakdown of automation impact</p>
        </div>
        <div className="p-6 space-y-6">
          {Object.values(timeSavingsData).map((category, catIndex) => (
            <div key={catIndex}>
              <h4 className="font-semibold text-[#e8f5e9] mb-3 flex items-center">
                <SafeIcon icon={FiZap} className="w-5 h-5 text-lime-500 mr-2" />
                {category.category}
              </h4>
              <div className="space-y-2">
                {category.tasks.map((task, taskIndex) => (
                  <motion.div
                    key={taskIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (catIndex * 0.1) + (taskIndex * 0.05) }}
                    className="bg-[#0a0f0d] rounded-lg p-4 border border-[#243129]"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <h5 className="font-medium text-[#e8f5e9]">{task.name}</h5>
                        <p className="text-sm text-[#6b8772]">{task.description}</p>
                      </div>
                      <div className="text-right ml-4">
                        <div className="flex items-center space-x-2 text-sm text-[#6b8772] mb-1">
                          <span className="line-through">{task.manualHours}h</span>
                          <span>→</span>
                          <span>{task.automatedHours}h</span>
                        </div>
                        <div className="text-lg font-bold text-lime-500">-{task.savings}h</div>
                        <div className="text-xs text-[#6b8772]">N$ {(task.savings * hourlyRate).toLocaleString()}/mo</div>
                      </div>
                    </div>
                    <div className="w-full bg-[#1c2822] rounded-full h-2 mt-2">
                      <div
                        className="bg-lime-500 h-2 rounded-full"
                        style={{ width: `${(task.savings / task.manualHours) * 100}%` }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Accuracy Improvements */}
      <div className="bg-[#111916] rounded-xl border border-[#243129] p-6">
        <h3 className="text-lg font-semibold text-[#e8f5e9] mb-4">Accuracy Improvements</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accuracyMetrics.map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="bg-[#0a0f0d] rounded-lg p-4 border border-[#243129]"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-[#e8f5e9]">{metric.metric}</h4>
                <span className="text-sm font-bold text-lime-500">+{metric.improvement}%</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <div className="flex justify-between text-xs text-[#6b8772] mb-1">
                    <span>Before</span>
                    <span>{metric.before}%</span>
                  </div>
                  <div className="w-full bg-[#1c2822] rounded-full h-2">
                    <div
                      className="bg-gray-500 h-2 rounded-full"
                      style={{ width: `${metric.before}%` }}
                    />
                  </div>
                </div>
                <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-lime-500" />
                <div className="flex-1">
                  <div className="flex justify-between text-xs text-[#6b8772] mb-1">
                    <span>After</span>
                    <span>{metric.after}%</span>
                  </div>
                  <div className="w-full bg-[#1c2822] rounded-full h-2">
                    <div
                      className="bg-lime-500 h-2 rounded-full"
                      style={{ width: `${metric.after}%` }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Exception Monitoring */}
      <div className="bg-[#111916] rounded-xl border border-[#243129] p-6">
        <h3 className="text-lg font-semibold text-[#e8f5e9] mb-4">Exception Monitoring & Prevention</h3>
        <p className="text-sm text-[#6b8772] mb-6">Automated alerts catching costly errors before they happen</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {exceptionStats.month.map((exception, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4"
            >
              <div className="flex items-start space-x-3">
                <div className="bg-amber-500/20 rounded-lg p-2">
                  <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-amber-500" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-[#e8f5e9] mb-1">{exception.type}</h4>
                  <div className="text-2xl font-bold text-amber-500 mb-1">{exception.caught}</div>
                  <p className="text-xs text-[#6b8772]">Prevented: {exception.prevented}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-6 bg-lime-500/10 border border-lime-500/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-lime-500" />
              <div>
                <h4 className="font-medium text-[#e8f5e9]">Total Issues Prevented</h4>
                <p className="text-sm text-[#6b8772]">Automated monitoring this month</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-lime-500">
                {exceptionStats.month.reduce((sum, e) => sum + e.caught, 0)}
              </div>
              <p className="text-sm text-[#6b8772]">exceptions caught</p>
            </div>
          </div>
        </div>
      </div>

      {/* ROI Summary */}
      <div className="bg-gradient-to-br from-lime-700/80 to-lime-800 rounded-xl p-8 text-white shadow-xl border border-lime-600/30">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold mb-2">Return on Investment Summary</h3>
            <p className="text-lime-200">Comprehensive automation impact analysis</p>
          </div>
          <SafeIcon icon={FiActivity} className="w-12 h-12 opacity-75" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
            <p className="text-lime-200 text-sm mb-1">Implementation Cost</p>
            <p className="text-3xl font-bold">N$ {(implementationCost / 1000).toFixed(0)}k</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
            <p className="text-lime-200 text-sm mb-1">Monthly Savings</p>
            <p className="text-3xl font-bold">N$ {(totalFinancialSavings / 1000).toFixed(0)}k</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
            <p className="text-lime-200 text-sm mb-1">Payback Period</p>
            <p className="text-3xl font-bold">{paybackMonths.toFixed(1)} months</p>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-lime-500/40">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lime-200 text-sm mb-1">First Year Net Benefit</p>
              <p className="text-4xl font-bold">N$ {((totalFinancialSavings * 12 - implementationCost - monthlyCost * 12) / 1000).toFixed(0)}k</p>
            </div>
            <div className="text-right">
              <p className="text-lime-200 text-sm mb-1">3-Year ROI</p>
              <p className="text-4xl font-bold">{(((totalFinancialSavings * 36 - implementationCost - monthlyCost * 36) / implementationCost) * 100).toFixed(0)}%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Savings;
